import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getToken, setToken as setTokenHelper, removeToken } from '../utils/tokenHelper';
import { setLogoutCallback } from '../utils/axiosConfig';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  company: string;
  role: string;
  industry: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  register: (userData: RegisterData) => Promise<boolean>;
  logout: () => void;
  loading: boolean;
  isAuthenticated: boolean;
}

interface RegisterData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  company: string;
  industry: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for stored authentication data
    const storedToken = getToken();
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      try {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        removeToken();
      }
    }
    setLoading(false);
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      setLoading(true);
      
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (response.ok) {
        const data = await response.json();
        
        const user: User = {
          id: data.user.id,
          email: data.user.email,
          firstName: data.user.firstName || data.user.fullName?.split(' ')[0] || 'User',
          lastName: data.user.lastName || data.user.fullName?.split(' ')[1] || '',
          company: data.user.company || '',
          role: data.user.role || 'user',
          industry: data.user.industry || 'Technology'
        };
        
        setToken(data.token);
        setUser(user);
        setTokenHelper(data.token);
        localStorage.setItem('user', JSON.stringify(user));
        
        setLoading(false);
        return true;
      } else {
        const error = await response.json();
        console.error('Login failed:', error.message);
        setLoading(false);
        return false;
      }
    } catch (error) {
      console.error('Login error:', error);
      setLoading(false);
      return false;
    }
  };

  const register = async (userData: RegisterData): Promise<boolean> => {
    try {
      setLoading(true);
      
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: userData.email,
          password: userData.password,
          fullName: `${userData.firstName} ${userData.lastName}`,
          company: userData.company,
          industry: userData.industry,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        
        const user: User = {
          id: data.user.id,
          email: data.user.email,
          firstName: userData.firstName,
          lastName: userData.lastName,
          company: userData.company || '',
          role: data.user.role || 'user',
          industry: userData.industry || 'Technology'
        };
        
        setToken(data.token);
        setUser(user);
        setTokenHelper(data.token);
        localStorage.setItem('user', JSON.stringify(user));
        
        setLoading(false);
        return true;
      } else {
        const error = await response.json();
        console.error('Registration failed:', error.message);
        setLoading(false);
        return false;
      }
    } catch (error) {
      console.error('Registration error:', error);
      setLoading(false);
      return false;
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    removeToken();
  };

  // Setup axios interceptor to handle token expiration
  useEffect(() => {
    setLogoutCallback(logout);
  }, []);

  const value: AuthContextType = {
    user,
    token,
    login,
    register,
    logout,
    loading,
    isAuthenticated: !!user && !!token,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
